import { Component, computed, inject, signal } from "@angular/core";
import { FaxPipe } from "@kno2/common/pipes";
import { SessionService } from "@kno2/core/session";
import { CachedAddressesService } from "@kno2/data-access/cached-addresses";
import { ContactsService } from "@kno2/data-access/contacts";
import { ReleaseTypesService } from "@kno2/data-access/release-types";
import { UsersService } from "@kno2/data-access/users";
import { CachedAddressesButtonComponent } from "@kno2/features/cached-addresses";
import { FindProviderService } from "@kno2/features/find-provider";
import { MessageFormService } from "@kno2/features/message/shared/message-form.service";
import { FeaturesService } from "@kno2/shared/features";
import { FormAutocompleteComponent, FormSelectComponent, FormService, FormTagsSelectComponent } from "@kno2/shared/forms";
import { InfoPopoverComponent } from "@kno2/shared/info-popover";

@Component({
    selector: "kno2-release-address-panel",
    imports: [FormAutocompleteComponent, FormSelectComponent, FormTagsSelectComponent, CachedAddressesButtonComponent, InfoPopoverComponent],
    providers: [FaxPipe],
    templateUrl: "./release-address-panel.component.html"
})
export class ReleaseAddressPanelComponent {
    private readonly messageFormService = inject(MessageFormService);
    private readonly usersService = inject(UsersService);
    private readonly contactsService = inject(ContactsService);
    private readonly releaseTypesService = inject(ReleaseTypesService);
    private readonly sessionService = inject(SessionService);
    private readonly cachedAddressesService = inject(CachedAddressesService);
    private readonly featuresService = inject(FeaturesService);
    private readonly findProviderService = inject(FindProviderService);
    private readonly formService = inject(FormService);
    private readonly faxPipe = inject(FaxPipe);

    public addressPanelForm = this.messageFormService.form;

    protected readonly canSelectFax = computed(() => this.featuresService.isEnabled("FaxDocumentSource"));
    protected readonly contactsSearchQuery = signal({ search: "" });

    protected readonly userId = computed(() => this.sessionService.profileData().userId);
    protected readonly documentSources = this.usersService.getDocumentSources(this.userId);
    protected readonly contacts = this.contactsService.searchContacts(this.contactsSearchQuery);
    protected readonly releaseTypes = this.releaseTypesService.getReleaseTypes(signal({ pageSize: 5000 }));
    protected readonly fromAddressOptions = computed(() =>
        (this.documentSources() || []).map(({ fullAddress, type }) => {
            const display = type === "Fax" ? `${this.faxPipe.transform(fullAddress)}` : fullAddress;
            return [display, fullAddress];
        })
    );
    protected readonly toAddressOptions = computed(() => {
        const result: { label: string; text?: string }[] = [];
        const seenValues = new Set<string>();

        (this.contacts()?.items || []).forEach(({ name, company, value }) => {
            if (!seenValues.has(value)) {
                result.push({ label: `${name} | ${company} | ${value}`, text: `${value}|${name}|${company}` });
                seenValues.add(value);
            }
        });

        (this.cachedAddressesService.getAddresses() || []).forEach((address) => {
            if (!seenValues.has(address)) {
                result.push({ label: address, text: address });
                seenValues.add(address);
            }
        });

        (this.documentSources() || []).forEach(({ fullAddress }) => {
            if (!seenValues.has(fullAddress)) {
                result.push({ label: fullAddress, text: fullAddress });
                seenValues.add(fullAddress);
            }
        });

        return result;
    });
    protected readonly releaseTypeOptions = computed(() => {
        const systemReleaseTypes = this.releaseTypes()?.releaseTypes.filter((releaseType) => !releaseType.organizationId);
        const organizationReleaseTypes = this.releaseTypes()?.releaseTypes.filter((releaseType) => releaseType.organizationId);

        return [
            {
                value: "Organization",
                items: organizationReleaseTypes || []
            },
            {
                label: "<hr />",
                value: "System",
                items: systemReleaseTypes || []
            }
        ];
    });

    public onToAddressSearchChange(search: string): void {
        this.contactsSearchQuery.set({ search });
    }

    public async findProvider(): Promise<void> {
        const values = await this.findProviderService.findProvider((value: string) => ({ label: value, text: value }));
        this.formService.appendValuesToArrayControl(this.addressPanelForm.controls.toAddresses, values);
    }
}
